import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { getData, postData, putData, deleteData } from "../../../FetchApiServices";
import { useToast, useConfirm, usePermissions } from "../Layout/T2TUIProvider";

// ─── Constants ────────────────────────────────────────────────────────────
const FIELD_TYPES = [
  { value: "text",     label: "Text" },
  { value: "textarea", label: "Long Text" },
  { value: "number",   label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "date",     label: "Date" },
  { value: "email",    label: "Email" },
  { value: "select",   label: "Dropdown" },
  { value: "radio",    label: "Radio" },
  { value: "checkbox", label: "Checkbox" },
  { value: "file",     label: "File Upload" },
];
const FIELD_TYPES_WITH_OPTIONS = new Set(["select", "radio", "checkbox"]);

const VALIDATOR_OPTIONS = [
  { value: "pan_format",   label: "PAN format" },
  { value: "gst_format",   label: "GSTIN format" },
  { value: "email_format", label: "Email format" },
  { value: "phone_format", label: "Phone format" },
];

const CATEGORY_OPTIONS = [
  { value: "procurement", label: "Procurement" },
  { value: "finance",     label: "Finance" },
  { value: "hr",          label: "HR" },
  { value: "legal",       label: "Legal" },
  { value: "it",          label: "IT" },
  { value: "general",     label: "General" },
];

const EMPTY_FIELD = {
  field_key: "", label: "", field_type: "text", is_required: false,
  options: null, validators: [], placeholder: "", help_text: "",
  sort_order: 0, sensitive: false,
};

const EMPTY_FORM = {
  code: "", name: "", description: "", category: "general",
  status: 1, is_system: 0, fields: [],
};

// ─── Helpers ──────────────────────────────────────────────────────────────
const slugify = (s) => String(s || "").trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
// Slug for option value: same rules but allows hyphens (matches typical select-option conventions)
const slugifyOption = (s) => String(s || "").trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "");

// Thin "+ Insert field here" divider — clickable strip that splices a new field at a given index.
// Hidden when canManage is false. Stays subtle by default, brightens on hover/focus.
const InsertFieldDivider = ({ onClick, label = "Insert field here" }) => (
  <button
    type="button"
    className="t2t-insert-field-divider"
    onClick={onClick}
    aria-label={label}
    title={label}
  >
    <span className="t2t-insert-field-divider-line" />
    <span className="t2t-insert-field-divider-pill">+ {label}</span>
    <span className="t2t-insert-field-divider-line" />
  </button>
);
InsertFieldDivider.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string,
};

// ─── Component ────────────────────────────────────────────────────────────
const T2TRequestTypesTab = () => {
  const toast   = useToast();
  const confirm = useConfirm();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("request_types.manage");

  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const [modal,      setModal]      = useState(null); // null | "new" | request_type_id
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [formError,  setFormError]  = useState("");

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    const resp = await getData("t2t/request-types");
    if (resp.error) setError(resp.error.message || "Failed to load request types.");
    else setList(Array.isArray(resp.data?.data) ? resp.data.data : []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openModal = async (id = null) => {
    setFormError("");
    if (id === null) { setForm(EMPTY_FORM); setModal("new"); return; }
    const resp = await getData(`t2t/request-types/${id}`);
    if (resp.error) { toast(resp.error.message || "Failed to load", "error"); return; }
    const d = resp.data?.data;
    if (!d) { toast("Request type not found", "error"); return; }
    setForm({
      code:        d.code || "",
      name:        d.name || "",
      description: d.description || "",
      category:    d.category || "general",
      status:      d.status ?? 1,
      is_system:   d.is_system || 0,
      fields: (d.fields || []).map((f) => ({
        field_key:   f.field_key,
        label:       f.label,
        field_type:  f.field_type,
        is_required: f.is_required === 1,
        options:     f.options || null,
        validators:  Array.isArray(f.validators) ? f.validators : [],
        placeholder: f.placeholder || "",
        help_text:   f.help_text || "",
        sort_order:  f.sort_order,
        sensitive:   f.sensitive === 1,
      })),
    });
    setModal(id);
  };

  const save = async () => {
    if (!form.name.trim()) { setFormError("Name is required"); return; }
    if (modal === "new" && !slugify(form.code)) { setFormError("Code is required"); return; }
    if (!form.fields.length) { setFormError("Add at least one field"); return; }
    const seenKeys = new Set();
    for (const f of form.fields) {
      if (!slugify(f.field_key)) { setFormError(`Every field needs a key (in "${f.label || "unnamed"}")`); return; }
      if (!f.label?.trim())       { setFormError(`Every field needs a label (key "${f.field_key}")`);    return; }
      const k = slugify(f.field_key);
      if (seenKeys.has(k)) {
        setFormError(`Two fields share the key "${k}" — keys must be unique within a request type`);
        return;
      }
      seenKeys.add(k);
      // Option fields must have at least one filled option, no blanks, no dupes
      if (FIELD_TYPES_WITH_OPTIONS.has(f.field_type)) {
        const opts = Array.isArray(f.options) ? f.options : [];
        const filled = opts.filter((o) => (o.label || "").trim() && (o.value || "").trim());
        if (filled.length === 0) {
          setFormError(`Field "${f.label}" needs at least one option`);
          return;
        }
        if (filled.length !== opts.length) {
          setFormError(`Field "${f.label}" has an option with an empty label or value — fill it in or remove the row`);
          return;
        }
        const seen = new Set();
        for (const o of filled) {
          const v = o.value.trim().toLowerCase();
          if (seen.has(v)) {
            setFormError(`Field "${f.label}" has duplicate option value "${o.value}" — values must be unique`);
            return;
          }
          seen.add(v);
        }
      }
    }
    setFormError("");
    setSaving(true);
    const payload = {
      ...(modal === "new" && { code: slugify(form.code) }),
      name:        form.name.trim(),
      description: form.description || "",
      category:    form.category || null,
      status:      Number(form.status),
      fields: form.fields.map((f, i) => ({
        field_key:   slugify(f.field_key),
        label:       f.label.trim(),
        field_type:  f.field_type,
        is_required: !!f.is_required,
        options:     FIELD_TYPES_WITH_OPTIONS.has(f.field_type) && Array.isArray(f.options) && f.options.length ? f.options : null,
        validators:  Array.isArray(f.validators) ? f.validators : [],
        placeholder: f.placeholder || "",
        help_text:   f.help_text   || "",
        sort_order:  i,
        sensitive:   !!f.sensitive,
      })),
    };
    const resp = modal === "new"
      ? await postData("t2t/request-types", payload)
      : await putData(`t2t/request-types/${modal}`, payload);
    if (resp.error) { setFormError(resp.error.message || "Failed to save"); setSaving(false); return; }
    toast(modal === "new" ? "Request type created" : "Request type updated", "success");
    setModal(null);
    setSaving(false);
    fetchList();
  };

  const remove = async (rt) => {
    if (rt.is_system === 1) { toast("System request types cannot be deleted", "warning"); return; }
    const ok = await confirm(`Delete "${rt.name}"? This cannot be undone.`, "Delete request type");
    if (!ok) return;
    const resp = await deleteData(`t2t/request-types/${rt.request_type_id}`);
    if (resp.error) toast(resp.error.message || "Failed to delete", "error");
    else { toast("Request type deleted", "success"); fetchList(); }
  };

  // ─── Field-level handlers ────────────────────────────────────────────────
  const addField  = () => setForm((f) => ({ ...f, fields: [...f.fields, { ...EMPTY_FIELD, sort_order: f.fields.length }] }));
  const insertField = (insertAt) => setForm((f) => {
    const next = [...f.fields];
    const idx = Math.max(0, Math.min(insertAt, next.length));
    next.splice(idx, 0, { ...EMPTY_FIELD });
    return { ...f, fields: next };
  });
  const patchField = (idx, patch) => setForm((f) => ({ ...f, fields: f.fields.map((x, i) => i === idx ? { ...x, ...patch } : x) }));
  const removeField = (idx) => setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== idx) }));
  const moveField   = (idx, dir) => setForm((f) => {
    const next = [...f.fields];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return f;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    return { ...f, fields: next };
  });
  const toggleValidator = (idx, vtype) => setForm((f) => ({
    ...f,
    fields: f.fields.map((x, i) => {
      if (i !== idx) return x;
      const has = (x.validators || []).some((v) => v.type === vtype);
      return { ...x, validators: has
        ? x.validators.filter((v) => v.type !== vtype)
        : [...(x.validators || []), { type: vtype }] };
    }),
  }));

  // ─── Option-level handlers (for select/radio/checkbox fields) ────────────
  const addOption = (fieldIdx) => setForm((f) => ({
    ...f,
    fields: f.fields.map((x, i) => i === fieldIdx
      ? { ...x, options: [...(Array.isArray(x.options) ? x.options : []), { value: "", label: "" }] }
      : x),
  }));
  const patchOption = (fieldIdx, optIdx, patch) => setForm((f) => ({
    ...f,
    fields: f.fields.map((x, i) => {
      if (i !== fieldIdx) return x;
      const opts = Array.isArray(x.options) ? x.options : [];
      return { ...x, options: opts.map((o, j) => j === optIdx ? { ...o, ...patch } : o) };
    }),
  }));
  const removeOption = (fieldIdx, optIdx) => setForm((f) => ({
    ...f,
    fields: f.fields.map((x, i) => {
      if (i !== fieldIdx) return x;
      const opts = Array.isArray(x.options) ? x.options : [];
      return { ...x, options: opts.filter((_, j) => j !== optIdx) };
    }),
  }));

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>
          Request types define the form schema a requester fills in. Workflows process requests of a given type.
        </div>
        {canManage && (
          <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={() => openModal()}>+ Add Request Type</button>
        )}
      </div>

      {loading && <div style={{ textAlign: "center", padding: 24, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading request types...</div>}
      {error   && <div style={{ padding: 12, color: "var(--t2t-danger)", fontSize: 13, background: "#FEF2F2", borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      {!loading && !error && (
        <div className="t2t-admin-table">
          {list.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: "var(--t2t-text-muted)", fontSize: 13 }}>No request types yet.</div>
          ) : (
            list.map((rt) => (
              <div className="t2t-admin-row" key={rt.request_type_id}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                    {rt.name}
                    {rt.is_system === 1 && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#1B3A6B", background: "#DBEAFE", padding: "2px 6px", borderRadius: 4 }}>SYSTEM</span>
                    )}
                    {rt.status === 0 && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#92400E", background: "#FEF3C7", padding: "2px 6px", borderRadius: 4 }}>INACTIVE</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-secondary)", marginTop: 2 }}>
                    <code style={{ fontSize: 10, background: "#F1F5F9", padding: "1px 4px", borderRadius: 3 }}>{rt.code}</code>
                    {rt.category && <span style={{ marginLeft: 8 }}>Category: {rt.category}</span>}
                  </div>
                  {rt.description && (
                    <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 4 }}>{rt.description}</div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => openModal(rt.request_type_id)}>
                    {canManage ? "Edit" : "View"}
                  </button>
                  {canManage && rt.is_system !== 1 && (
                    <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "var(--t2t-danger)" }} onClick={() => remove(rt)}>Delete</button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ─── Create / Edit modal ───────────────────────────────────────── */}
      {modal !== null && (
        <div className="t2t-overlay" onClick={() => !saving && setModal(null)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="t2t-modal-head">
              <div>
                <div className="t2t-modal-title">
                  {modal === "new" ? "New Request Type" : (canManage ? "Edit Request Type" : "View Request Type")}
                </div>
                <div className="t2t-modal-sub">
                  {modal === "new"
                    ? "Define the form schema a requester will fill in."
                    : `Code: ${form.code}${form.is_system === 1 ? " (system-defined)" : ""}`}
                </div>
              </div>
              <button className="t2t-close-btn" onClick={() => !saving && setModal(null)}>×</button>
            </div>

            <div className="t2t-modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {formError && (
                <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
                  {formError}
                </div>
              )}

              {/* ── Metadata ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="t2t-fg">
                  <label>Name *</label>
                  <input className="t2t-fc" value={form.name} disabled={!canManage}
                    onChange={(e) => setForm({ ...form, name: e.target.value, code: modal === "new" && !form.code ? slugify(e.target.value) : form.code })}
                    placeholder="e.g. Vendor Creation" />
                </div>
                <div className="t2t-fg">
                  <label>Code *</label>
                  <input className="t2t-fc" value={form.code} disabled={modal !== "new" || !canManage}
                    onChange={(e) => setForm({ ...form, code: slugify(e.target.value) })}
                    placeholder="e.g. vendor_creation" />
                </div>
                <div className="t2t-fg">
                  <label>Category</label>
                  <select className="t2t-fc" value={form.category} disabled={!canManage}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="t2t-fg">
                  <label>Status</label>
                  <select className="t2t-fc" value={form.status} disabled={!canManage}
                    onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>
              <div className="t2t-fg">
                <label>Description</label>
                <textarea className="t2t-fc" value={form.description} disabled={!canManage} rows={2}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What business process is this request type for?" />
              </div>

              {/* ── Fields ── */}
              <div style={{ borderTop: "1px dashed var(--t2t-border)", paddingTop: 14, marginTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <label style={{ fontWeight: 700, fontSize: 13 }}>Form Fields *</label>
                  {canManage && (
                    <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={addField}>+ Add Field</button>
                  )}
                </div>

                {form.fields.length === 0 && (
                  <div style={{ textAlign: "center", padding: 16, fontSize: 12, color: "var(--t2t-text-muted)", background: "#F8FAFC", borderRadius: 6 }}>
                    No fields yet. {canManage && "Click + Add Field to start."}
                  </div>
                )}

                {form.fields.map((f, idx) => (
                  <React.Fragment key={idx}>
                    {canManage && <InsertFieldDivider onClick={() => insertField(idx)} />}
                    <div style={{ border: "1px solid var(--t2t-border)", borderRadius: 8, padding: 12, marginBottom: 10, background: "#FAFAFA" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-muted)" }}>#{idx + 1}</span>
                      <div style={{ flex: 1 }} />
                      {canManage && (
                        <>
                          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => moveField(idx, -1)} disabled={idx === 0} title="Move up">↑</button>
                          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => moveField(idx, 1)}  disabled={idx === form.fields.length - 1} title="Move down">↓</button>
                          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "var(--t2t-danger)" }} onClick={() => removeField(idx)}>Remove</button>
                        </>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      <div className="t2t-fg">
                        <label>Label *</label>
                        <input className="t2t-fc" value={f.label} disabled={!canManage}
                          onChange={(e) => {
                            const newLabel = e.target.value;
                            // Smart auto-sync: Key follows Label as long as Key is empty
                            // OR Key still equals slugify(previousLabel) (i.e. was auto-generated, not manually overridden).
                            const prevSlug = slugify(f.label || "");
                            const keyShouldAutoSync = !f.field_key || f.field_key === prevSlug;
                            patchField(idx, keyShouldAutoSync
                              ? { label: newLabel, field_key: slugify(newLabel) }
                              : { label: newLabel });
                          }}
                          placeholder="e.g. Vendor Name" />
                      </div>
                      <div className="t2t-fg">
                        <label>Key *</label>
                        <input className="t2t-fc" value={f.field_key} disabled={!canManage}
                          onChange={(e) => patchField(idx, { field_key: slugify(e.target.value) })}
                          placeholder="e.g. vendor_name" />
                      </div>
                      <div className="t2t-fg">
                        <label>Type</label>
                        <select className="t2t-fc" value={f.field_type} disabled={!canManage}
                          onChange={(e) => patchField(idx, { field_type: e.target.value, options: FIELD_TYPES_WITH_OPTIONS.has(e.target.value) ? (f.options || []) : null })}>
                          {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                    </div>

                    {FIELD_TYPES_WITH_OPTIONS.has(f.field_type) && (
                      <div className="t2t-fg" style={{ marginTop: 4 }}>
                        <label>Options <span style={{ color: "var(--t2t-text-muted)", fontWeight: 400 }}>(label shown to user; value stored in DB)</span></label>
                        {(!Array.isArray(f.options) || f.options.length === 0) ? (
                          <div style={{
                            padding: "12px 14px",
                            border: "1px dashed var(--t2t-border)",
                            borderRadius: 6,
                            fontSize: 12,
                            color: "var(--t2t-text-muted)",
                            textAlign: "center",
                            background: "#FAFBFC",
                          }}>
                            No options yet. Click <strong>+ Add option</strong> below to create the first one.
                          </div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {f.options.map((opt, oi) => (
                              <div key={oi} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, alignItems: "center" }}>
                                <input
                                  className="t2t-fc"
                                  disabled={!canManage}
                                  value={opt.label || ""}
                                  placeholder="Label (e.g. Goods)"
                                  onChange={(e) => {
                                    const newLabel = e.target.value;
                                    // Auto-slug Value if Value is empty OR still equals the previous slug-of-label
                                    const prevSlug = slugifyOption(opt.label || "");
                                    const valueShouldAutoFill = !opt.value || opt.value === prevSlug;
                                    patchOption(idx, oi, valueShouldAutoFill
                                      ? { label: newLabel, value: slugifyOption(newLabel) }
                                      : { label: newLabel });
                                  }}
                                />
                                <input
                                  className="t2t-fc"
                                  disabled={!canManage}
                                  value={opt.value || ""}
                                  placeholder="Value (e.g. goods)"
                                  onChange={(e) => patchOption(idx, oi, { value: slugifyOption(e.target.value) })}
                                />
                                {canManage && (
                                  <button
                                    type="button"
                                    className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                                    style={{ color: "var(--t2t-danger)" }}
                                    onClick={() => removeOption(idx, oi)}
                                    title="Remove option"
                                    aria-label={`Remove option ${opt.label || opt.value || oi + 1}`}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {canManage && (
                          <button
                            type="button"
                            className="t2t-btn t2t-btn-outline t2t-btn-sm"
                            style={{ marginTop: 8 }}
                            onClick={() => addOption(idx)}
                          >
                            + Add option
                          </button>
                        )}
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6, fontSize: 12 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: canManage ? "pointer" : "default" }}>
                        <input type="checkbox" checked={!!f.is_required} disabled={!canManage}
                          onChange={(e) => patchField(idx, { is_required: e.target.checked })} />
                        Required
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: canManage ? "pointer" : "default" }} title="Mask this field in audit logs">
                        <input type="checkbox" checked={!!f.sensitive} disabled={!canManage}
                          onChange={(e) => patchField(idx, { sensitive: e.target.checked })} />
                        Sensitive (mask in logs)
                      </label>
                    </div>

                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>
                        Validators
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 12 }}>
                        {VALIDATOR_OPTIONS.map((v) => {
                          const checked = (f.validators || []).some((x) => x.type === v.value);
                          return (
                            <label key={v.value} style={{ display: "flex", alignItems: "center", gap: 5, cursor: canManage ? "pointer" : "default" }}>
                              <input type="checkbox" checked={checked} disabled={!canManage}
                                onChange={() => toggleValidator(idx, v.value)} />
                              {v.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  </React.Fragment>
                ))}
                {canManage && form.fields.length > 0 && (
                  <InsertFieldDivider onClick={() => insertField(form.fields.length)} />
                )}
              </div>
            </div>

            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              {canManage && (
                <button className="t2t-btn t2t-btn-primary" onClick={save} disabled={saving}>
                  {saving ? "Saving..." : (modal === "new" ? "Create" : "Save Changes")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default T2TRequestTypesTab;
